import { useEffect, useState, useCallback } from "react";
import api from "../api";
import { useI18n } from "../i18n";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { eur } from "../helpers";
import { Field } from "../components/FormField";
import ExpenseManager from "../components/ExpenseManager";
import { FileDown, FileSpreadsheet, Wallet, TrendingUp, TrendingDown, Percent, Zap, Home, Landmark, Trophy, FileText, RefreshCw } from "lucide-react";

const ADMIN_ROLES = ["super_admin", "admin", "amministratore"];
const now = new Date();
const COMPANY_FIELDS = [["commercialista", "Commercialista"], ["banca", "Banca"], ["software", "Software"], ["telefono", "Telefono"], ["assicurazioni", "Assicurazioni"], ["marketing", "Marketing"], ["consulenze", "Consulenze"], ["tasse", "Tasse"], ["cedolare", "Cedolare/Imposte"], ["iva", "IVA"], ["altri", "Altri costi generali"]];

export default function Accounting() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const [tab, setTab] = useState("dashboard");
  const [reportView, setReportView] = useState("apartment");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [report, setReport] = useState(null);
  const [apts, setApts] = useState([]);
  // cost forms
  const [companyCost, setCompanyCost] = useState({});
  const [ownerReports, setOwnerReports] = useState([]);

  const monthLabel = (m) => new Date(2020, m - 1, 1).toLocaleString(lang === "it" ? "it-IT" : "en-US", { month: "long" });
  const years = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

  const loadReport = useCallback(() => {
    api.get("/accounting/report", { params: { year, month } }).then((r) => setReport(r.data)).catch(() => setReport(null));
  }, [year, month]);

  useEffect(() => { loadReport(); }, [loadReport]);
  useEffect(() => { api.get("/apartments/lookup").then((r) => setApts(r.data)); }, []);

  const loadCompanyCost = useCallback(() => {
    api.get("/company-costs", { params: { year, month } }).then((r) => setCompanyCost(r.data[0] || { year, month })).catch(() => setCompanyCost({ year, month }));
  }, [year, month]);
  useEffect(() => { if (tab === "companycosts" && isAdmin) loadCompanyCost(); }, [tab, isAdmin, loadCompanyCost]);

  const loadOwnerReports = useCallback(() => {
    api.get("/owner-reports").then((r) => setOwnerReports(r.data)).catch(() => setOwnerReports([]));
  }, []);
  useEffect(() => { if (tab === "reports") loadOwnerReports(); }, [tab, loadOwnerReports]);

  const generateReports = async () => {
    try {
      const r = await api.post("/owner-reports/generate", null, { params: { year, month } });
      toast.success(`${t("reportsGenerated")}: ${r.data.generated}`); loadOwnerReports();
    } catch { toast.error("Error"); }
  };

  const downloadOwnerReport = async (rep) => {
    try {
      const res = await api.get(`/owner-reports/${rep.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `report_${rep.owner_name.replace(/ /g, "_")}_${rep.year}_${String(rep.month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Download error"); }
  };

  const saveCompanyCost = async () => {
    try {
      const payload = { ...companyCost, year, month };
      COMPANY_FIELDS.forEach(([k]) => { payload[k] = +(payload[k] || 0); });
      await api.post("/company-costs", payload);
      toast.success(t("costsSaved")); loadReport(); loadCompanyCost();
    } catch { toast.error("Error"); }
  };

  const download = async (fmt) => {
    try {
      const res = await api.get(`/accounting/report/${fmt}`, { params: { year, month }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `report_${year}_${String(month).padStart(2, "0")}.${fmt === "csv" ? "csv" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Export error"); }
  };

  const s = report?.summary;
  const kpis = s ? [
    { icon: Wallet, label: t("revenueMonth"), value: eur(s.revenue), tone: "bg-primary text-primary-foreground" },
    { icon: TrendingUp, label: t("netProfitK"), value: eur(s.net_profit), tone: s.net_profit >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700" },
    { icon: Trophy, label: t("mostProfitable"), value: s.most_profitable ? s.most_profitable.name : "—", sub: s.most_profitable ? eur(s.most_profitable.value) : "", tone: "bg-amber-100 text-amber-700" },
    { icon: TrendingDown, label: t("lossMaking"), value: s.loss_making ? s.loss_making.name : "—", sub: s.loss_making ? eur(s.loss_making.value) : "", tone: "bg-rose-100 text-rose-700" },
    { icon: Percent, label: t("totalOta"), value: eur(s.total_ota_commission), tone: "bg-violet-100 text-violet-700" },
    { icon: Zap, label: t("totalUtilities"), value: eur(s.total_utilities), tone: "bg-cyan-100 text-cyan-700" },
    { icon: Home, label: t("totalCanoni"), value: eur(s.total_canoni), tone: "bg-slate-100 text-slate-700" },
    ...(s.total_taxes != null ? [{ icon: Landmark, label: t("totalTaxes"), value: eur(s.total_taxes), tone: "bg-orange-100 text-orange-700" }] : []),
    { icon: TrendingUp, label: t("avgMargin"), value: `${s.avg_margin}%`, tone: "bg-emerald-100 text-emerald-700" },
  ] : [];

  const tabs = [
    { k: "dashboard", label: t("accountingDash") },
    { k: "report", label: t("monthlyReport") },
    { k: "aptcosts", label: t("apartmentCostsTab") },
    ...(isAdmin ? [{ k: "companycosts", label: t("companyCostsTab") }] : []),
    ...(isAdmin ? [{ k: "reports", label: t("generatedReports") }] : []),
  ];

  return (
    <div className="space-y-6 fade-in" data-testid="accounting-page">
      <Card className="flex flex-wrap items-end justify-between gap-4 border-slate-200 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t("month")} className="min-w-[140px]">
            <Select value={String(month)} onValueChange={(v) => setMonth(+v)}>
              <SelectTrigger data-testid="acc-month"><SelectValue /></SelectTrigger>
              <SelectContent>{Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <SelectItem key={m} value={String(m)} className="capitalize">{monthLabel(m)}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("year")} className="min-w-[100px]">
            <Select value={String(year)} onValueChange={(v) => setYear(+v)}>
              <SelectTrigger data-testid="acc-year"><SelectValue /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <div className="flex gap-2">
          <Button data-testid="export-pdf-btn" variant="outline" className="gap-2" onClick={() => download("pdf")}><FileDown size={16} /> {t("exportPdf")}</Button>
          <Button data-testid="export-csv-btn" variant="outline" className="gap-2" onClick={() => download("csv")}><FileSpreadsheet size={16} /> {t("exportCsv")}</Button>
        </div>
      </Card>

      <div className="inline-flex flex-wrap rounded-lg border border-slate-200 bg-white p-1">
        {tabs.map((tb) => (
          <button key={tb.k} data-testid={`acc-tab-${tb.k}`} onClick={() => setTab(tb.k)}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${tab === tb.k ? "bg-primary text-primary-foreground" : "text-slate-600 hover:bg-slate-100"}`}>
            {tb.label}
          </button>
        ))}
      </div>

      {/* Dashboard */}
      {tab === "dashboard" && s && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-4">
          {kpis.map((c, i) => (
            <Card key={i} data-testid={`acc-kpi-${i}`} className="flex items-center gap-4 border-slate-200 p-5">
              <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${c.tone}`}><c.icon size={20} /></div>
              <div className="min-w-0"><div className="truncate text-xl font-semibold text-slate-900 font-head">{c.value}</div><div className="text-xs text-slate-500">{c.label}</div>{c.sub ? <div className="text-xs font-medium text-slate-600">{c.sub}</div> : null}</div>
            </Card>
          ))}
        </div>
      )}

      {/* Report */}
      {tab === "report" && report && (
        <div className="space-y-4">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
            <button data-testid="report-view-apartment" onClick={() => setReportView("apartment")} className={`rounded-md px-3 py-1.5 text-sm font-medium ${reportView === "apartment" ? "bg-primary text-primary-foreground" : "text-slate-600"}`}>{t("byApartment")}</button>
            <button data-testid="report-view-owner" onClick={() => setReportView("owner")} className={`rounded-md px-3 py-1.5 text-sm font-medium ${reportView === "owner" ? "bg-primary text-primary-foreground" : "text-slate-600"}`}>{t("byOwner")}</button>
            {report.show_company && <button data-testid="report-view-company" onClick={() => setReportView("company")} className={`rounded-md px-3 py-1.5 text-sm font-medium ${reportView === "company" ? "bg-primary text-primary-foreground" : "text-slate-600"}`}>{t("companyReport")}</button>}
          </div>

          {reportView === "apartment" && (
            <Card className="overflow-hidden border-slate-200">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-3">{t("apartment")}</th><th className="px-3 py-3">{t("owner")}</th>
                      <th className="px-3 py-3">{t("occupancy")}</th><th className="px-3 py-3">{t("grossRevenue")}</th>
                      <th className="px-3 py-3">{t("otaCommission")}</th><th className="px-3 py-3">{t("stripeCommission")}</th>
                      <th className="px-3 py-3">{t("netRevenue")}</th><th className="px-3 py-3">{t("directCosts")}</th>
                      <th className="px-3 py-3">{t("grossMargin")}</th>
                      {report.show_company && <th className="px-3 py-3">{t("quotaGeneral")}</th>}
                      {report.show_company && <th className="px-3 py-3">{t("operatingProfit")}</th>}
                      <th className="px-3 py-3">{t("ownerPayout")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.apartments.map((a) => (
                      <tr key={a.apartment_id} data-testid={`acc-apt-${a.apartment_id}`} className="border-b border-slate-100">
                        <td className="px-3 py-3 font-medium text-slate-900">{a.name}</td>
                        <td className="px-3 py-3 text-slate-600">{a.owner_name}</td>
                        <td className="px-3 py-3 text-slate-600">{a.occupancy}%</td>
                        <td className="px-3 py-3 text-slate-900">{eur(a.gross_revenue)}</td>
                        <td className="px-3 py-3 text-violet-700">{eur(a.ota_commission)}</td>
                        <td className="px-3 py-3 text-violet-700">{eur(a.stripe_commission)}</td>
                        <td className="px-3 py-3 text-slate-900">{eur(a.net_revenue)}</td>
                        <td className="px-3 py-3 text-amber-700">{eur(a.direct_costs)}</td>
                        <td className={`px-3 py-3 font-medium ${a.gross_margin >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{eur(a.gross_margin)}</td>
                        {report.show_company && <td className="px-3 py-3 text-slate-600">{eur(a.quota_general)}</td>}
                        {report.show_company && <td className={`px-3 py-3 font-medium ${a.operating_profit >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{eur(a.operating_profit)}</td>}
                        <td className={`px-3 py-3 font-semibold ${a.owner_payout >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{eur(a.owner_payout)}</td>
                      </tr>
                    ))}
                    {report.apartments.length === 0 && <tr><td colSpan={12} className="px-4 py-8 text-center text-slate-400">{t("noData")}</td></tr>}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {reportView === "owner" && (
            <Card className="overflow-hidden border-slate-200">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-3">{t("owner")}</th><th className="px-3 py-3">{t("apartments")}</th>
                      <th className="px-3 py-3">{t("grossRevenue")}</th><th className="px-3 py-3">{t("netRevenue")}</th>
                      <th className="px-3 py-3">{t("directCosts")}</th><th className="px-3 py-3">{t("mgmtCommission")}</th>
                      <th className="px-3 py-3">{t("ownerPayout")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.owners.map((o) => (
                      <tr key={o.owner_id} data-testid={`acc-owner-${o.owner_id}`} className="border-b border-slate-100">
                        <td className="px-3 py-3 font-medium text-slate-900">{o.name}</td>
                        <td className="px-3 py-3 text-xs text-slate-500">{o.apartments.join(", ")}</td>
                        <td className="px-3 py-3 text-slate-900">{eur(o.gross_revenue)}</td>
                        <td className="px-3 py-3 text-slate-900">{eur(o.net_revenue)}</td>
                        <td className="px-3 py-3 text-amber-700">{eur(o.direct_costs)}</td>
                        <td className="px-3 py-3 text-violet-700">{eur(o.management_commission)}</td>
                        <td className={`px-3 py-3 font-semibold ${o.owner_payout >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{eur(o.owner_payout)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {reportView === "company" && report.company && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <Card className="border-slate-200 p-5" data-testid="acc-company-summary">
                <h3 className="mb-3 font-head font-semibold text-slate-900">{report.company_name}</h3>
                {[["grossRevenue", report.company.gross_revenue], ["otaCommission", report.company.ota_commission], ["stripeCommission", report.company.stripe_commission], ["netRevenue", report.company.net_revenue], ["directCosts", report.company.direct_costs], ["generalCostsTotal", report.company.general_costs_total], ["grossMargin", report.company.gross_margin], ["ownerPayout", report.company.owner_payout]].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-slate-100 py-2 text-sm"><span className="text-slate-600">{t(k)}</span><span className="font-medium text-slate-900">{eur(v)}</span></div>
                ))}
                <div className="mt-2 flex justify-between rounded-lg bg-emerald-50 px-3 py-3"><span className="font-semibold text-emerald-800">{t("operatingProfit")}</span><span className={`font-bold ${report.company.operating_profit >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{eur(report.company.operating_profit)}</span></div>
              </Card>
              <Card className="border-slate-200 p-5">
                <h3 className="mb-3 font-head font-semibold text-slate-900">{t("companyCostsTab")}</h3>
                {COMPANY_FIELDS.map(([k, label]) => (
                  <div key={k} className="flex justify-between border-b border-slate-100 py-2 text-sm"><span className="text-slate-600">{label}</span><span className="font-medium text-slate-900">{eur(report.company.general_costs_breakdown[k])}</span></div>
                ))}
              </Card>
            </div>
          )}
        </div>
      )}

      {/* Apartment costs — line-item expenses */}
      {tab === "aptcosts" && (
        <ExpenseManager onChanged={loadReport} />
      )}

      {/* Company costs entry */}
      {tab === "companycosts" && isAdmin && (
        <Card className="max-w-2xl border-slate-200 p-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {COMPANY_FIELDS.map(([k, label]) => (
              <Field key={k} label={label}><Input data-testid={`companycost-${k}`} type="number" value={companyCost[k] ?? 0} onChange={(e) => setCompanyCost((f) => ({ ...f, [k]: +e.target.value }))} /></Field>
            ))}
          </div>
          <div className="mt-6 flex justify-end">
            <Button data-testid="save-companycost-btn" onClick={saveCompanyCost} className="bg-primary hover:bg-primary/90">{t("save")}</Button>
          </div>
        </Card>
      )}
      {/* Generated owner reports (admin) */}
      {tab === "reports" && isAdmin && (
        <Card className="border-slate-200 p-5" data-testid="generated-reports">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-slate-500">{t("autoReportInfo")}</p>
            <Button data-testid="generate-reports-btn" onClick={generateReports} className="gap-2 bg-primary hover:bg-primary/90"><RefreshCw size={16} /> {t("generateNow")}</Button>
          </div>
          <div className="space-y-2">
            {ownerReports.map((r) => (
              <div key={r.id} data-testid={`owner-report-${r.id}`} className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-rose-100 text-rose-600"><FileText size={16} /></div>
                  <div>
                    <div className="text-sm font-medium text-slate-900">{r.owner_name}</div>
                    <div className="text-xs text-slate-500 capitalize">{monthLabel(r.month)} {r.year}</div>
                  </div>
                </div>
                <Button size="sm" variant="outline" className="gap-1" data-testid={`download-report-${r.id}`} onClick={() => downloadOwnerReport(r)}><FileDown size={14} /> {t("downloadPdf")}</Button>
              </div>
            ))}
            {ownerReports.length === 0 && <p className="text-sm text-slate-400">{t("noReports")}</p>}
          </div>
        </Card>
      )}
    </div>
  );
}
